import axios from './client'

/**
 * Low-level Discogs API wrappers.
 *
 * These keep the raw `axios` calls in one place so the React Query hooks
 * (useCollection / useWantlist) and the sync orchestration in DiscogsContext
 * stay declarative. The page-by-page sync + cleanup protocol lives in
 * `runDiscogsSync` so its retry/batching semantics are preserved verbatim.
 */

export interface DiscogsLocalItem {
  basic_info?: { id?: number }
  release_id?: number
  date_added?: string
}

export interface DiscogsListData {
  /** Release IDs currently in the list (local DB mirror of Discogs). */
  ids: number[]
  /** Map of release ID → ISO date the item was added on Discogs. */
  dates: Record<number, string>
}

export interface SyncProgress {
  type: 'wantlist' | 'collection' | null
  current: number
  total: number
  totalItems: number
}

export const SYNC_BATCH_SIZE = 5

type SyncList = 'wantlist' | 'collection'

function isTransientSyncError(error: any) {
  const status = error?.response?.status
  return status === 408 || status === 429 || status >= 500
}

/** Retry an idempotent sync step when Discogs (or the gateway) is briefly unavailable. */
async function postSyncStep(list: SyncList, body: Record<string, unknown>) {
  let lastError: unknown
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      return await axios.post(`/discogs/sync/${list}`, body)
    } catch (error) {
      lastError = error
      if (!isTransientSyncError(error) || attempt === 2) throw error
      await new Promise(resolve => setTimeout(resolve, 750 * (attempt + 1)))
    }
  }
  throw lastError
}

/**
 * Fetch the connected Discogs username from the app's own profile endpoint.
 * Returns null when the user is not logged in or has no Discogs link.
 */
export async function getDiscogsProfile(): Promise<{ username: string } | null> {
  const { data } = await axios.get('/users/me')
  if (data.success && data.user?.discogs_username) {
    return { username: data.user.discogs_username }
  }
  return null
}

export async function startDiscogsConnect(): Promise<{
  success: boolean
  authorizeUrl?: string
  configured?: boolean
  message?: string
}> {
  const { data } = await axios.get('/auth/discogs/connect')
  return data
}

export async function disconnectDiscogs(): Promise<void> {
  await axios.delete('/users/me/discogs')
}

/**
 * Read the local DB mirror of a list (collection or wantlist), paging through
 * every page so we materialise the full ID + date set. This is the fast,
 * read-only step run after a sync (or on mount) — it never hits Discogs.
 */
export async function getLocalDiscogsList(list: SyncList): Promise<DiscogsListData> {
  const ids: number[] = []
  const dates: Record<number, string> = {}
  let page = 1
  let totalPages = 1

  while (page <= totalPages) {
    const { data } = await axios.get(`/discogs/${list}/local?page=${page}&per_page=200`)
    if (!data.success) break
    totalPages = data.total_pages ?? 1
    for (const item of (data.items ?? []) as DiscogsLocalItem[]) {
      const id = item.basic_info?.id ?? item.release_id
      if (id) {
        ids.push(id)
        if (item.date_added) dates[id] = item.date_added
      }
    }
    page++
  }

  return { ids, dates }
}

/**
 * Drive a full Discogs sync for one list, page by page, reporting progress
 * through `onProgress`. Mirrors the hardened protocol exactly:
 *   Phase 1 — POST each page (with batchSize + carried syncToken) until done.
 *   Phase 2 — POST a `cleanup` request only when every page succeeded.
 *   Phase 3 — caller reloads IDs from the local DB (see getLocalDiscogsList).
 *
 * Returns the fresh list data and `partial` = true when one or more pages
 * failed (so the caller can surface a "sync was partial" error while still
 * keeping whatever was synced).
 */
export async function runDiscogsSync(
  list: SyncList,
  onProgress: (p: SyncProgress) => void,
): Promise<{ data: DiscogsListData; totalSynced: number; partial: boolean; failureMessage?: string }> {
  onProgress({ type: list, current: 0, total: 0, totalItems: 0 })

  // Phase 1: Sync from Discogs page by page (client-driven for live progress)
  let page = 1
  let totalPages = 1
  let totalSynced = 0
  let syncToken = ''
  let allPagesSucceeded = true
  let failureMessage: string | undefined

  while (page <= totalPages) {
    let data: any
    try {
      ({ data } = await postSyncStep(list, {
        page,
        batchSize: SYNC_BATCH_SIZE,
        ...(syncToken ? { syncToken } : {}),
      }))
    } catch (error: any) {
      allPagesSucceeded = false
      failureMessage = error?.response?.data?.message || `Discogs sync stopped at page ${page}. Your previously synced items are still available.`
      break
    }
    if (!data.success) {
      allPagesSucceeded = false
      failureMessage = data.message || `Discogs sync stopped at page ${page}. Your previously synced items are still available.`
      break
    }
    syncToken = data.syncToken
    totalPages = data.totalPages
    totalSynced += data.synced
    const syncedThroughPage = data.page ?? page
    onProgress({
      type: list,
      current: syncedThroughPage,
      total: totalPages,
      totalItems: data.totalItems ?? totalSynced,
    })
    page = data.nextPage ?? totalPages + 1
  }

  // Phase 2: Cleanup stale entries (only if all pages succeeded)
  if (allPagesSucceeded && syncToken) {
    await postSyncStep(list, { page: 'cleanup', syncToken })
  }

  // Phase 3: Load IDs from local DB (fast)
  const data = await getLocalDiscogsList(list)

  return { data, totalSynced, partial: !allPagesSucceeded, failureMessage }
}
